import { getLocalTimeZone } from '@internationalized/date'
import { Dialog, DialogContent } from '@mui/material'
import React, { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import NewCheckbox from 'ui/atoms/NewCheckbox/NewCheckbox'
import { SettlementPeriodSummary } from 'ui/components/Followup/SettlementPeriodSummary/SettlementPeriodSummary'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import NewModalButtons from 'ui/molecules/ModalButtons/NewModalButtons'
import Datetime from 'utils/datetime/datetime'
import { toSettlementOptimizationWindow } from './SettlementOptimizeButton.helper'

type SettlementOptimizeDialogProps = {
  period: Period
  subtypes: Subtype[]
  onClose: () => void
  onSubmit: (selectedSubtypes: string[]) => void
}

export function SettlementOptimizeDialog({
  period,
  subtypes,
  onClose,
  onSubmit,
}: SettlementOptimizeDialogProps): ReactNode {
  const { t } = useTranslation()
  const [selectedSubtypes, setSelectedSubtypes] = useState<Set<string>>(new Set())
  const optimizationWindow = toSettlementOptimizationWindow(period)

  function handleToggleSubtype(subtype: string): void {
    setSelectedSubtypes((current) => {
      const next = new Set(current)

      if (next.has(subtype)) {
        next.delete(subtype)
      } else {
        next.add(subtype)
      }

      return next
    })
  }

  function handleSubmit(): void {
    onSubmit([...selectedSubtypes])
    onClose()
  }

  const optimizesEveryScenario = selectedSubtypes.size === 0

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <CustomDialogTitle title={t(`Select settlement scenario to optimize`)} handleClose={onClose} />
      <DialogContent>
        <div className="mb-3">
          <SettlementPeriodSummary
            text={t(`The optimization runs from {{startTime}} to {{endTime}}, {{timeZone}} time.`, {
              startTime: Datetime.toLocalTime(optimizationWindow.startTime, `longDay`),
              endTime: Datetime.toLocalTime(Datetime.ISODatetimeTo59Minutes(optimizationWindow.endTime), `longDay`),
              timeZone: getLocalTimeZone(),
            })}
          />
        </div>
        <div className="flex flex-col">
          {subtypes.map((subtype) => (
            <div key={subtype.subtype} className="border-b border-grey-2">
              <NewCheckbox
                label={subtype.subtype_display_name}
                checked={selectedSubtypes.has(subtype.subtype)}
                onChange={() => handleToggleSubtype(subtype.subtype)}
              />
            </div>
          ))}
        </div>
        {optimizesEveryScenario && (
          <InfoBanner text={t(`All settlement scenarios will be optimized if you do not make a selection`)} />
        )}
        <NewModalButtons
          onCancel={onClose}
          onSubmit={handleSubmit}
          submitLabel={
            optimizesEveryScenario
              ? t(`Optimize all settlement scenarios`)
              : t(`Optimize {{count}} settlement scenario`, { count: selectedSubtypes.size })
          }
        />
      </DialogContent>
    </Dialog>
  )
}
